import React from "react";
import { Link } from "react-router-dom";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";

import PublicV2Layout from "../layout/PublicV2Layout";
import { SEO_V2 } from "../theme/seoMetaV2";
import useQuoteFormV2 from "../shared/useQuoteFormV2";
import {
  CssTextFieldStandardV2,
  CssTextFieldOutlineV2,
  MyFormHelperTextV2,
} from "../shared/CssTextFieldV2";
import QuoteRadioFormV2 from "../shared/QuoteRadioFormV2";
import TransitionsModalV2 from "../shared/TransitionsModalV2";
import PictureUpload from "../../components/PictureUpload/PictureUpload";
import Tracking from "../../components/TrackingComp";
import { v2Href } from "../publicRoutes";

import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import RecommendRoundedIcon from "@mui/icons-material/RecommendRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import "./quoteV2.css";

const PITCH = [
  { icon: <BoltRoundedIcon />, title: "Fast & Easy", desc: "Share a few details and we handle the rest." },
  { icon: <RecommendRoundedIcon />, title: "Expert Recommendations", desc: "Honest guidance from real refinishers." },
  { icon: <VerifiedRoundedIcon />, title: "No Obligation", desc: "A free quote with zero pressure." },
];

// Match the Home page stats exactly so the site doesn't contradict itself.
const STATS = [
  { value: "15+", label: "Years of Experience" },
  { value: "8,000+", label: "Doors Refinished" },
  { value: "5-Star", label: "Customer Rating" },
  { value: "100%", label: "Satisfaction Guaranteed" },
];

const QuoteV2 = (navProps) => {
  const { preview = false } = navProps;
  // Preview lands leads in the TEST db (/tgraphql); the public site submits to
  // the live endpoint (omit submitContextUri → global client).
  const q = useQuoteFormV2(preview ? { submitContextUri: "/tgraphql" } : {});

  return (
    <PublicV2Layout seo={SEO_V2.quote} {...navProps}>
      {/* convertQuote flips true on a valid submit → records the conversion */}
      <Tracking page={"quote"} convertQuote={q.gotQuoted} data-nosnippet />
      <TransitionsModalV2
        modalOpen={q.modalOpen}
        handleClose={q.handleClose}
        modalText={q.modalText}
        redirectTo={v2Href(preview, "home")}
      />

      <section className="bdrv2-section bdrv2-quote-sec">
        <div className="bdrv2-container bdrv2-quote-grid">
          {/* LEFT — pitch */}
          <div className="bdrv2-quote-pitch">
            <p className="bdrv2-eyebrow bdrv2-accent-text">Request a Quote</p>
            <h1 className="bdrv2-quote-h1">Let&apos;s refinish something beautiful.</h1>
            <p className="bdrv2-quote-lead">
              Tell us about your project and we&apos;ll provide a customized quote
              tailored to your needs.
            </p>
            <ul className="bdrv2-quote-pitch-list">
              {PITCH.map((p) => (
                <li key={p.title}>
                  <span className="bdrv2-quote-pitch-ico bdrv2-accent-text">
                    {p.icon}
                  </span>
                  <span>
                    <strong>{p.title}</strong>
                    <em>{p.desc}</em>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT — form */}
          <div className="bdrv2-quote-form bdrv2-glass">
            <h2 className="bdrv2-quote-form-title">Request a Quote</h2>

            <CssTextFieldStandardV2
              sx={{ width: "100%", my: 1 }}
              label="Name"
              name="nameInput"
              variant="standard"
              value={q.formState.nameInput}
              onChange={q.handleChange}
            />

            <CssTextFieldStandardV2
              sx={{ width: "100%", my: 1 }}
              label="Email"
              name="emailInput"
              variant="standard"
              value={q.formState.emailInput}
              onChange={q.handleChange}
              onBlur={q.checkError}
              onFocus={q.clearError}
            />
            <MyFormHelperTextV2
              errorText="Please enter a valid email."
              formError={q.errorState.emailInput}
            />

            {/* City w/ Google Places autocomplete */}
            <div className="bdrv2-quote-city" ref={q.cityInputRef}>
              <CssTextFieldStandardV2
                sx={{ width: "100%", my: 1 }}
                label="City where work is needed"
                name="cityInput"
                variant="standard"
                autoComplete="off"
                value={q.formState.cityInput}
                onChange={q.handleChange}
                onKeyDown={q.handleCityKeyDown}
                onFocus={() => {
                  q.clearError("cityInput");
                  if (q.formState.cityInput.length >= 2)
                    q.setShowCitySuggestions(true);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    q.setShowCitySuggestions(false);
                    q.setCityHighlight(-1);
                  }, 200);
                }}
              />
              {q.showCitySuggestions && q.citySuggestions.length > 0 && (
                <div className="bdrv2-quote-city-drop">
                  {q.citySuggestions.map((s, i) => (
                    <div
                      key={s.place_id}
                      className={`bdrv2-quote-city-item${
                        i === q.cityHighlight ? " active" : ""
                      }`}
                      onMouseDown={() => q.handleCitySelect(s.description)}
                      onMouseEnter={() => q.setCityHighlight(i)}
                    >
                      {s.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <MyFormHelperTextV2
              errorText="Please enter the city where you need work done."
              formError={q.errorState.cityInput}
            />

            {/* Phone + type */}
            <div className="bdrv2-quote-phone-row">
              <CssTextFieldStandardV2
                sx={{ width: "62%", my: 1 }}
                type="tel"
                label="Phone Number"
                name="phoneNumberInput"
                variant="standard"
                inputProps={{ maxLength: 12, inputMode: "numeric" }}
                value={q.formState.phoneNumberInput}
                onChange={q.handleChange}
                onBlur={q.checkError}
                onFocus={q.clearError}
              />
              <FormControl variant="standard" sx={{ m: 1, width: "32%", alignSelf: "flex-end" }}>
                <InputLabel
                  sx={{
                    color: "var(--v2-accent-text)",
                    "&.Mui-focused": { color: "var(--v2-accent-text)" },
                  }}
                >
                  Type
                </InputLabel>
                <Select
                  name="numberType"
                  value={q.formState.numberType}
                  onChange={q.handleChange}
                  onBlur={q.checkError}
                  onFocus={() => q.clearError("numberType")}
                  sx={{
                    color: "var(--v2-field-text)",
                    "& .MuiSvgIcon-root": { color: "var(--v2-field-text)" },
                    "&:before": { borderBottomColor: "var(--v2-border-strong)" },
                  }}
                >
                  <MenuItem value="Home">Home</MenuItem>
                  <MenuItem value="Cell">Cell</MenuItem>
                  <MenuItem value="Work">Work</MenuItem>
                  <MenuItem value="Spouse">Spouse</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </div>
            <MyFormHelperTextV2
              errorText="Please enter valid phone number"
              formError={q.errorState.phoneNumberInput}
            />

            {/* Text consent */}
            <div className="bdrv2-quote-consent">
              <Checkbox
                sx={{
                  color: "var(--v2-text-faint)",
                  "&.Mui-checked": { color: "var(--v2-accent)" },
                  p: 0.5,
                }}
                checked={q.formState.textConsent}
                onChange={q.handleChange}
                name="textConsent"
              />
              <div className="bdrv2-quote-consent-text">
                I agree to receive text messages from Biggin&apos;s Door
                Refinishing at the number provided. This can include marketing or
                quote/appointment details. Reply STOP to opt out. Msg &amp; Data
                rates apply.
                <div className="bdrv2-quote-consent-links">
                  <Link to={v2Href(preview, "privacy")} className="bdrv2-accent-text">
                    Privacy Policy
                  </Link>
                  <span> | </span>
                  <Link to={v2Href(preview, "terms")} className="bdrv2-accent-text">
                    Terms of Service
                  </Link>
                </div>
              </div>
            </div>

            {/* Choices */}
            <QuoteRadioFormV2
              choiceStateFunction={q.handleChange}
              name="intExtChoice"
              question="Would it be just the exterior that you want refinished?"
              formId="bdrv2-door-side"
              choiceDefault="Exterior"
              choices={["Exterior", "Interior", "Both Sides"]}
            />
            <QuoteRadioFormV2
              choiceStateFunction={q.handleChange}
              name="discountChoice"
              question="Would a military, senior, teacher or first responder discount apply?"
              formId="bdrv2-discount"
              choiceDefault="None"
              choices={["Military", "Senior", "Teacher", "First responder", "None"]}
            />
            <QuoteRadioFormV2
              choiceStateFunction={q.handleChange}
              name="heardAboutUs"
              question="How did you hear about us?"
              formId="bdrv2-heard"
              choiceDefault={null}
              choices={[
                "TheHomeMag",
                "Friend, Family or Neighbor",
                "Met a refinisher",
                "Next Door App",
                "Google",
                "Other",
              ]}
              endComponent={
                <Collapse in={q.formState.heardAboutUs === "Other"}>
                  <CssTextFieldOutlineV2
                    sx={{ width: "100%", mt: 1 }}
                    label="Other"
                    name="otherInput"
                    value={q.formState.otherInput}
                    onChange={q.handleChange}
                    onBlur={q.checkError}
                    onFocus={q.clearError}
                  />
                  <MyFormHelperTextV2
                    errorText="Please enter a response."
                    formError={q.errorState.otherInput}
                  />
                </Collapse>
              }
            />

            {/* Photo upload — reused unchanged from prod */}
            <div className="bdrv2-quote-upload">
              <h3 className="bdrv2-quote-upload-title">
                Upload a photo of your door (optional)
              </h3>
              <PictureUpload
                changeSticky={q.changeSticky}
                changeLoading={q.changeLoading}
                changePhotoIds={q.changePhotoIds}
              />
            </div>

            <p className="bdrv2-quote-tos-note">
              By submitting, you agree to Biggin&apos;s Door Refinishing{" "}
              <Link to={v2Href(preview, "terms")} className="bdrv2-accent-text">
                Terms of Service
              </Link>
              .
            </p>

            <button
              type="button"
              className="bdrv2-btn bdrv2-btn--primary bdrv2-quote-submit"
              disabled={q.loadingSaved}
              onClick={q.sendContactEmail}
            >
              Submit Request
            </button>
          </div>
        </div>

        {/* Stat bar */}
        <div className="bdrv2-container bdrv2-quote-stats">
          {STATS.map((s) => (
            <div key={s.label} className="bdrv2-quote-stat">
              <span className="bdrv2-quote-stat-value bdrv2-accent-text">
                {s.value}
              </span>
              <span className="bdrv2-quote-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>
    </PublicV2Layout>
  );
};

export default QuoteV2;
